import React, { useState, useMemo, useRef, useEffect, useContext, useCallback } from "react";
import {
  Box,
  TableBody,
  Typography,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Rating,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import { useLocation } from 'react-router-dom';
import { formatCurrency } from '../utils/currencyUtils';
import { applySalesMultiplier } from '../utils/chartUtils';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BarChart } from "@mui/x-charts/BarChart";
import { formatChartValue, formatAxisValue } from '../utils/formatUtils';
import { colors } from '../utils/themeUtils';
import { NoMaxWidthTooltip, TooltipContent, CommentText } from './common/SharedTooltip';
import { ReviewsContext } from '../context/ReviewsContext';
import { BusinessContext } from "../context/BusinessContext";

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (string) => {
  if (!string || typeof string !== "string") return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Helper function to format source name for display
const formatSourceName = (source) => {
  if (!source) return "N/A";
  
  // Handle specific sources with custom formatting
  switch (source.toLowerCase()) {
    case 'google':
      return 'Google';
    case 'justeat':
      return 'Just Eat';
    case 'deliveroo':
      return 'Deliveroo';
    case 'ubereats':
      return 'Uber Eats';
    case 'designmynight':
      return 'Design My Night';
    case 'opentable':
      return 'OpenTable';
    case 'trustpilot':
      return 'Trustpilot';
    default:
      return capitalizeFirstLetter(source);
  }
};

// Source logo component - displays logo when available, text fallback for unknown platforms
const SourceLogo = ({ source }) => {
  const [imageError, setImageError] = useState(false);
  const logoPath = getSourceLogo(source);
  
  // If no logo path available or image failed to load, show text fallback
  if (!logoPath || imageError) {
    return (
      <Typography variant="body2" sx={{ 
        fontWeight: 600, 
        color: getSourceColor(source),
        fontSize: { xs: '0.75rem', md: '0.875rem' },
        textAlign: 'center'
      }}>
        {formatSourceName(source)}
      </Typography>
    );
  }
  
  // Display logo for known platforms
  return (
    <Box 
      component="img" 
      src={logoPath}
      alt={formatSourceName(source)} 
      onError={() => setImageError(true)}
      sx={{ 
        width: { xs: '48px', md: '60px' }, 
        height: { xs: '48px', md: '60px' }, 
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto'
      }} 
    />
  );
};

// Helper function to get source logo path
const getSourceLogo = (source) => {
  if (!source) return null;
  
  switch (source.toLowerCase()) {
    case 'google':
      return '/images/sources/google-logo.png';
    case 'justeat':
    case 'just eat':
      return '/images/sources/justeat-logo.png';
    case 'deliveroo':
      return '/images/sources/deliveroo-logo.png';
    case 'ubereats':
    case 'uber eats':
      return '/images/sources/ubereats-logo.png';
    case 'tripadvisor':
      return '/images/sources/tripadvisor-logo.png';

    case 'trustpilot':
      return '/images/sources/trustpilot-logo.png';
    case 'booking':
    case 'booking.com':
      return '/images/sources/booking-logo.png';
    case 'yelp':
      return '/images/sources/yelp-logo.png';
    case 'designmynight':
    case 'design my night':
      return '/images/sources/designmynight-logo.png';
    case 'opentable':
      return '/images/sources/opentable-logo.png';
    default:
      return null;
  }
};

// Helper function to get source color
const getSourceColor = (source) => {
  if (!source) return '#546E7A';
  
  switch (source.toLowerCase()) {
    case 'google':
      return '#4285f4'; // Google blue
    case 'justeat':
      return '#ff6600'; // Just Eat orange
    case 'deliveroo':
      return '#00ccbc'; // Deliveroo teal
    case 'ubereats':
      return '#000000'; // UberEats black
    case 'yelp':
      return '#d32323'; // Yelp red
    case 'designmynight':
    case 'design my night':
      return '#8e24aa'; // Design My Night purple
    case 'opentable':
      return '#da3743'; // OpenTable red
    case 'trustpilot':
      return '#00b67a'; // Trustpilot green
    default:
      return '#546E7A'; // Default gray
  }
};

// Define colors for different sentiments
const sentimentColors = {
  positive: colors.positiveGreen,  // Green for positive sentiments
  negative: colors.negativeRed,    // Red for negative sentiments
  default: colors.textPrimary, // Default text color
};

// Custom hook to measure container width and determine which columns to display
const useResponsiveColumns = (containerRef) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [columns, setColumns] = useState({
    showReviewer: true,
    showDate: true,
    showRatingStars: true,
    showSource: true
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Setup ResizeObserver
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    // Cleanup
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Determine which columns to show based on actual available width
  useEffect(() => {
    const newColumnState = {
      showReviewer: containerWidth > 450,
      // Always show date column regardless of width
      showDate: true,
      // Always show rating stars, even on mobile
      showRatingStars: true,
      // Show source column on screens wider than 350px
      showSource: containerWidth > 350
    };
    setColumns(newColumnState);
  }, [containerWidth]);

  return columns;
};

const ReviewsTable = ({
  currencySymbol = "$",
  highlightCategory = null,
  highlightSubject = null,
  showDateColumn = true,
  showBusinessName = false,
  businessName = "Dummy Business",
  businessAddress = "Dummy Address",
}) => {
  // Get data from context
  const {
    // filteredReviewsData, // Use the C/S filtered data -- THIS WILL BE REPLACED
    // analysisData,        // THIS WILL BE REPLACED by row.analyses
    // isReviewsLoading,    // THIS WILL BE REPLACED by isTableReviewsLoading
    // isAnalysisLoading,   // THIS WILL BE REPLACED by isTableReviewsLoading
    view,
    selectedBusiness, // This might still be useful for single business specific info if any
    selectedCategory, // Added for filter dependency
    selectedSubject,  // Added for filter dependency
    selectedMonthRange, // THIS WILL BE REMOVED from filtering logic here - backend handles it
    selectedSources, // Added for source filter dependency
    // NEW CONTEXT VALUES FOR TABLE:
    selectedReviewId,       // Get selectedReviewId from context
    tableReviews,           // The actual review data for the table
    tableReviewsPage,
    tableReviewsHasMore,
    isTableReviewsLoading,  // Loading state specifically for the table
    tableReviewsError,
    fetchTableReviews,      // Function to fetch/load more table reviews
    REVIEWS_TABLE_PAGE_SIZE
  } = useContext(ReviewsContext);

  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-width: 900px)');
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const isPortrait = useMediaQuery('(orientation: portrait)');

  // Get selected businesses count from BusinessContext
  const { selectedBusinesses } = useContext(BusinessContext);
  const numSelectedBusinesses = selectedBusinesses?.length || 0;
  const showBusinessColumn = numSelectedBusinesses > 1;

  const location = useLocation();

  // Filter by selectedMonthRange and then sort
  const finalReviewsToDisplay = useMemo(() => {
    // Ensure tableReviews is an array before sorting
    if (!Array.isArray(tableReviews)) return [];

    // Backend already filters by date range (selectedMonthRange or timeframe) and sorts by date.
    // The data (tableReviews) from context is already filtered and sorted as needed for display.
    // No additional client-side date filtering or primary sorting is needed here.
    return tableReviews; // Directly use the paginated and filtered data from context

  }, [tableReviews]); // Only depend on tableReviews

  // Use custom hook to determine which columns to display
  const { showReviewer, showDate, showRatingStars, showSource } = useResponsiveColumns(tableContainerRef);

  // Format impact values with proper precision (for Rating view)
  const formatImpactExact = (impact) => {
    if (impact === undefined || impact === null) return '0.000 ★';
    
    const absImpact = Math.abs(impact);
    
    // For zero values
    if (absImpact === 0) return '0.000 ★';
    
    // For normal values (≥ 0.001), use standard 3 decimal places
    if (absImpact >= 0.001) {
      return `${absImpact.toFixed(3)} ★`;
    }
    
    // For very small values, find the first significant digit
    const scientificNotation = absImpact.toExponential();
    const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
    
    if (matches) {
      const firstDigit = matches[1]; // First significant digit
      const exponent = parseInt(matches[3], 10);
      
      // Create the string with leading zeros
      let result = '0.';
      for (let i = 1; i < exponent; i++) {
        result += '0';
      }
      result += firstDigit;
      
      return `${result} ★`;
    }
    
    // Fallback to standard formatting
    return `${absImpact.toFixed(3)} ★`;
  };

  // Updated highlightReviewText function
  const highlightReviewText = (
    text, 
    rowAnalyses, // NEW: Pass analyses from the row (expected to be review.analyses)
    currencySymbol, 
    view, 
    rowTurnover, 
    highlightCategory,
    highlightSubject
  ) => {
    if (!text) return [];

    // 1. Get analysis items for this specific review
    const analysisForThisReview = rowAnalyses || []; // Use analyses passed with the row
    if (!analysisForThisReview.length) {
      // If no analysis, return plain text (or handle as needed)
      return [text];
    }

    // 2. Group analysis items by their comment text
    const groupedAnalysis = analysisForThisReview.reduce((acc, item) => {
      (acc[item.comment] = acc[item.comment] || []).push(item);
      return acc;
    }, {});

    // 3. Sort distinct comments by their position in the review text
    const sortedComments = Object.keys(groupedAnalysis).sort(
      (a, b) => text.indexOf(a) - text.indexOf(b)
    );

    const elements = [];
    let lastIndex = 0;

    // Format impact value function - both revenue and impact values with proper precision
    const formatImpactValue = (item) => {
      // Handle zero values with no sign
      if (item.attribute_impact === 0) {
        return view === "Sales" ? formatCurrency(0, currencySymbol, view) : '0.000 ★';
      }
      
      // For Sales view, calculate revenue impact using turnover
      if (view === "Sales") {
        // Use the passed-in rowTurnover
        const numericValue = applySalesMultiplier([item.attribute_impact], view, rowTurnover)[0]; 
        const formattedValue = formatCurrency(numericValue, currencySymbol, view) || 'N/A';
        if (formattedValue === 'N/A') return 'N/A';
        const prefixedValue = numericValue > 0 ? `+${formattedValue}` : formattedValue;
        return prefixedValue;
      } else {
        // For Rating view, use our precision formatter
        const value = item.attribute_impact;
        const isPositive = value > 0;
        return `${isPositive ? '+' : '-'}${formatImpactExact(Math.abs(value))}`;
      }
    };

    sortedComments.forEach((comment, index) => {
      const commentIndex = text.indexOf(comment, lastIndex);
      if (commentIndex === -1) return; 

      if (commentIndex > lastIndex) {
        elements.push(text.substring(lastIndex, commentIndex));
      }

      const items = groupedAnalysis[comment];

      // Determine if this comment should be directly highlighted based on passed-in filters
      const shouldDirectlyHighlight = highlightCategory && items.some(item => {
        const categoryMatch = item.category?.toLowerCase() === highlightCategory.toLowerCase();
        const subjectMatch = highlightSubject && item.subject_group?.toLowerCase() === highlightSubject.toLowerCase();
        return categoryMatch && (!highlightSubject || subjectMatch);
      });

      // Determine if highlighting should be applied (either specific or always on)
      const applyHighlight = true;
      
      // Determine sentiment if highlighting is needed
      let sentiment = 'default'; // Default sentiment
      if (applyHighlight && items.length > 0) {
          const sentiments = items.map(item => item.sentiment?.toLowerCase());
          const hasPositive = sentiments.includes('positive');
          const hasNegative = sentiments.includes('negative');

          if (hasPositive && hasNegative) {
              sentiment = 'negative'; // Default to negative if both are present
          } else if (hasPositive) {
              sentiment = 'positive';
          } else if (hasNegative) {
              sentiment = 'negative';
          }
          // If neither positive nor negative, sentiment remains 'default'
      }

      // Render using CommentText for highlighting, otherwise plain text
      let commentElement;
      if (applyHighlight && (sentiment === 'positive' || sentiment === 'negative')) {
          commentElement = (
              <CommentText sentiment={sentiment}>
                  {comment}
              </CommentText>
          );
      } else {
          commentElement = comment; // Render plain text
      }

      // Always show tooltip if items exist, wrapping the determined element
      if (items.length > 0) {
        elements.push(
          <NoMaxWidthTooltip
            key={index}
            title={<TooltipContent items={items} formatImpactValue={formatImpactValue} options={{ view, currencySymbol }} />}
            arrow
            placement="top"
          >
            {/* Always wrap string children in a <span> for Tooltip compliance */}
            {typeof commentElement === 'string' ? <span>{commentElement}</span> : commentElement}
          </NoMaxWidthTooltip>
        );
      } else {
        // Render plain text if no analysis items (no tooltip needed)
        elements.push(commentElement);
      }

      lastIndex = commentIndex + comment.length;
    });

    if (lastIndex < text.length) {
      elements.push(text.substring(lastIndex));
    }

    // Return the array of elements directly, don't join
    return elements;
  };

  // Determine column count based on visibility
  let columnCount = 1; // Always show Review column
  if (showReviewer) columnCount++;
  if (showDate) columnCount++;
  if (showRatingStars) columnCount++;
  if (showSource) columnCount++; // Add source column if needed
  if (showBusinessColumn) columnCount++; // Add business column if needed

  // --- Infinite Scroll --- 
  const observer = useRef();
  const lastReviewElementRef = useCallback(node => {
    if (isTableReviewsLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && tableReviewsHasMore) {
        fetchTableReviews({ 
          pageToFetch: tableReviewsPage + 1, 
          resetData: false,
          sourcesToUse: selectedSources
        });
      }
    });
    if (node) observer.current.observe(node);
  }, [isTableReviewsLoading, tableReviewsHasMore, fetchTableReviews, tableReviewsPage, selectedSources]);
  // --- End Infinite Scroll ---

  // Construct the key for the Row, if business name is shown, include it to avoid conflicts with same review_id from different businesses
  const getRowKey = (review) => {
    let key = review.id;
    if (showBusinessColumn && review.business_name) {
      key = `${review.business_name}-${review.id}`;
    }
    return key;
  };

  // Callback for InfiniteScroll to load more items
  const loadMoreItems = useCallback(() => {
    if (!isTableReviewsLoading && tableReviewsHasMore) {
      fetchTableReviews({ 
        pageToFetch: tableReviewsPage + 1, 
        resetData: false,
        sourcesToUse: selectedSources
      });
    }
  }, [isTableReviewsLoading, tableReviewsHasMore, fetchTableReviews, tableReviewsPage, selectedSources]);

  // Effect for initial data fetch and when business selection or filters change
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlHasReviewId = urlParams.has('review_id');
    const reviewIdFromUrl = urlParams.get('review_id');

    const urlHasMonth = urlParams.has('month');
    const urlHasCategory = urlParams.has('category');
    const urlHasSubject = urlParams.has('subject_group');

    const filtersPotentiallyPending = 
      (urlHasMonth && !selectedMonthRange) ||
      (urlHasCategory && !selectedCategory) ||
      (urlHasSubject && !selectedSubject);

    const currentFiltersForLog = {
      category: selectedCategory,
      subject: selectedSubject,
      monthRange: selectedMonthRange
    };
    const businessesForLog = selectedBusinesses?.map(b => b.id).join(',') || selectedBusiness?.id || 'N/A';

    if (filtersPotentiallyPending) {
      return; // Don't fetch if critical filters from URL aren't set in context yet
    }

    // If selectedReviewId is present in context AND it matches the one in the URL (or URL has no review_id),
    // it implies context is up-to-date for review_id filtering.
    if (selectedReviewId && urlHasReviewId && selectedReviewId === reviewIdFromUrl) {
      fetchTableReviews({
        pageToFetch: 1, 
        resetData: true,
        reviewId: selectedReviewId,
        sourcesToUse: selectedSources
      });
    } else if (!urlHasReviewId && (selectedBusiness || (selectedBusinesses && selectedBusinesses.length > 0))) {
      fetchTableReviews({
        pageToFetch: 1,
        resetData: true,
        categoryToUse: selectedCategory,   // Pass current category
        subjectToUse: selectedSubject,     // Pass current subject
        monthRangeToUse: selectedMonthRange, // Pass current monthRange
        sourcesToUse: selectedSources      // Pass current sources
      });
    } else if (!urlHasReviewId) {
      fetchTableReviews({ 
        pageToFetch: 1, 
        resetData: true, 
        businessIdsToUse: [],
        sourcesToUse: selectedSources
      });
    } else {
      // Waiting for selectedReviewId in context to align with URL or other conditions for fetching.
    }
    // Ensure fetchTableReviews is stable or correctly memoized in context
  }, [selectedBusiness, selectedBusinesses, selectedCategory, selectedSubject, selectedMonthRange, selectedSources, fetchTableReviews, location, selectedReviewId]);

  // If loading, show skeletons or a loading spinner
  if (isTableReviewsLoading && (!tableReviews || tableReviews.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If there's an error, display it
  if (tableReviewsError) {
    return (
      <Box sx={{ padding: 2, color: 'error.main', textAlign: 'center' }}>
        <Typography variant="h6">Error loading reviews:</Typography>
        <Typography>{tableReviewsError}</Typography>
      </Box>
    );
  }

  return (
    <Card sx={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
      borderRadius: { xs: '12px', sm: '16px', md: '24px' },
      border: '1px solid rgba(25, 118, 210, 0.1)',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        // boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)' // REMOVED hover boxShadow
      },
      position: 'relative',
      overflow: 'hidden', // Keep overflow hidden on Card if needed for ::before
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
      }
    }}>
      <CardContent sx={{ 
        p: { 
          xs: isLandscape ? 0.5 : 1, 
          sm: 1.5, 
          md: 2 
        },
        '&:last-child': { 
          pb: { 
            xs: isLandscape ? 0.5 : 1, 
            sm: 1.5, 
            md: 2 
          } 
        }
      }}>
        {showBusinessName && (
          <Box sx={{
            mb: { 
              xs: isLandscape ? 0.5 : 1, 
              sm: 1.5, 
              md: 2 
            },
            display: 'flex',
            flexDirection: 'column',
            gap: isLandscape ? 0.25 : 0.5
          }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                color: '#0A2540',
                fontSize: { 
                  xs: isLandscape ? '0.9rem' : '1rem', 
                  sm: '1.1rem', 
                  md: '1.25rem' 
                }
              }}
            >
              {businessName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#546E7A',
                fontSize: { 
                  xs: isLandscape ? '0.75rem' : '0.8rem', 
                  sm: '0.85rem', 
                  md: '0.9rem' 
                }
              }}
            >
              {businessAddress}
            </Typography>
          </Box>
        )}

        <TableContainer 
          ref={tableContainerRef}
          sx={{ 
            mt: { 
              xs: isLandscape ? 0 : 0.5, 
              sm: 0.5, 
              md: 1 
            },
            // boxShadow: '0 4px 20px -5px rgba(0,0,0,0.1)', // Also remove shadow from TableContainer if present
            borderRadius: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#ccc',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#aaa'
            }
          }}
        >
          {/* Add loading indicator based on context loading states */} 
          {/* {(isReviewsLoading || isAnalysisLoading) && <Typography>Loading reviews...</Typography>} -- OLD */}
          {/* Display error message if tableReviewsError is set */}
          {tableReviewsError && (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              Error loading reviews: {tableReviewsError}
            </Typography>
          )}
          
          {/* Only render table if not initially loading AND no error OR if some data exists (for load more) */}
          {(!isTableReviewsLoading || finalReviewsToDisplay.length > 0) && !tableReviewsError && (
            <Table 
              aria-label="reviews table"
              sx={{ 
                tableLayout: 'fixed',
                '& th, & td': {
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Conditionally render Business column header FIRST */}
                  {showBusinessColumn && (
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '100px', sm: '150px', md: '200px' }, // Adjust width as needed
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                    }}>Business</TableCell>
                  )}

                  {showSource && (
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '100px', sm: '150px', md: '200px' },
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      textAlign: 'center'
                    }}>Platform</TableCell>
                  )}
                  
                  {showReviewer && (
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '80px', sm: '120px', md: '150px' },
                      whiteSpace: 'normal',
                      overflow: 'hidden'
                    }}>Reviewer</TableCell>
                  )}
                  
                  {showDate && (
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '70px', sm: '110px', md: '120px' },
                      minWidth: { xs: '70px', sm: '110px', md: '120px' },
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      overflow: 'hidden',
                      textOverflow: { xs: 'clip', sm: 'ellipsis' },
                      lineHeight: { xs: 1.2, sm: 'normal' }
                    }}>Date</TableCell>
                  )}
                  
                  {showRatingStars && (
                    <TableCell align="center" sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '65px', sm: '120px' },
                      padding: { xs: '8px 6px', sm: '16px' }
                    }}>Rating</TableCell>
                  )}
                  
                  {/* Review column is always visible and takes remaining space */}
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#0A2540',
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                    width: 'auto'
                  }}>
                    <Typography sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}>
                      Review
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              
              <TableBody>
                {finalReviewsToDisplay.length > 0 ? (
                  finalReviewsToDisplay.map((row, index) => {
                    // Find the business details corresponding to the current review row
                    // Assuming row has a business_id. If not, lookup might need adjustment (e.g., by name/address)
                    const businessForReview = selectedBusinesses?.find(b => b.id === row.business_id);
                    const rowTurnover = businessForReview?.turnover; // Get turnover specific to this row's business

                    // For infinite scroll, attach ref to the last element
                    if (finalReviewsToDisplay.length === index + 1) {
                      return (
                        <TableRow
                          ref={lastReviewElementRef} // Attach ref here
                          key={getRowKey(row)} // Use the constructed key
                          sx={{
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              transform: 'translateY(-2px)'
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
                              py: { xs: 1.5, sm: 2 } // Standardize padding slightly
                            }
                          }}
                        >
                          {/* Conditionally render Business cell data FIRST */}
                          {showBusinessColumn && (
                            <TableCell sx={{
                                color: '#0A2540',
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                whiteSpace: 'normal',
                                overflow: 'hidden'
                            }}>
                               <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.business_name || 'N/A'}</Typography>
                               <Typography variant="caption" sx={{ color: '#546E7A' }}>{row.business_address || ''}</Typography>
                            </TableCell>
                          )}

                          {showSource && (
                            <TableCell sx={{
                              color: getSourceColor(row.source),
                              fontWeight: 500,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              whiteSpace: 'normal',
                              overflow: 'hidden',
                              textAlign: 'center'
                            }}>
                                                          <SourceLogo source={row.source} />
                            </TableCell>
                          )}

                          {showReviewer && (
                            <TableCell sx={{
                              color: '#0A2540',
                              fontWeight: 500,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              whiteSpace: 'normal',
                              overflow: 'hidden'
                            }}>{row.profile_name}</TableCell>
                          )}
                          
                          {showDate && (
                            <TableCell sx={{
                              color: '#546E7A',
                              fontSize: { xs: '0.875rem', md: '0.95rem' },
                              width: { xs: '70px', sm: '110px', md: '120px' },
                              minWidth: { xs: '70px', sm: '110px', md: '120px' },
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              overflow: 'hidden',
                              textOverflow: { xs: 'clip', sm: 'ellipsis' },
                              lineHeight: { xs: 1.2, sm: 'normal' }
                            }}>
                              {isSmallScreen && !isLandscape ? (
                                <>
                                  {new Date(row.estimated_datetime).toLocaleDateString("en-GB").split('/').slice(0, 2).join('/')}/
                                  <br />
                                  {new Date(row.estimated_datetime).toLocaleDateString("en-GB").split('/')[2]}
                                </>                              
                              ) : (
                                new Date(row.estimated_datetime).toLocaleDateString("en-GB")
                              )}
                            </TableCell>
                          )}
                          
                          {showRatingStars && (
                            <TableCell align="center" sx={{
                              width: { xs: '65px', sm: '120px' },
                              padding: { xs: '8px 6px', sm: '16px' },
                              overflow: 'visible'
                            }}>
                              {isSmallScreen ? (
                                row.rating !== undefined && (
                                  <Typography sx={{
                                    color: '#0A2540',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.875rem', md: '1rem' }
                                  }}>
                                    {row.rating}
                                  </Typography>
                                )
                              ) : (
                                <Rating
                                  value={row.rating}
                                  readOnly
                                  precision={0.5}
                                  size="small"
                                  sx={{
                                    color: '#1976d2',
                                    '& .MuiRating-iconEmpty': {
                                      color: 'rgba(25, 118, 210, 0.3)'
                                    }
                                  }}
                                />
                              )}
                            </TableCell>
                          )}

                          {/* Review cell is always visible */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#0A2540',
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                lineHeight: { 
                                  xs: 1.4,
                                  sm: 1.5,
                                  md: 1.6 
                                }
                              }}
                            >
                              {highlightReviewText(
                                row.review_text,
                                row.analyses, // Pass analyses from the row
                                currencySymbol,
                                view,
                                rowTurnover,
                                highlightCategory,
                                highlightSubject
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    // Standard row rendering for other rows
                    return (
                      <TableRow
                        key={getRowKey(row)} // Use the constructed key
                        sx={{
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            transform: 'translateY(-2px)'
                          },
                          '& td': {
                            borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
                            py: { xs: 1.5, sm: 2 } // Standardize padding slightly
                          }
                        }}
                      >
                        {/* Conditionally render Business cell data FIRST */}
                        {showBusinessColumn && (
                          <TableCell sx={{
                              color: '#0A2540',
                              fontWeight: 500,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              whiteSpace: 'normal',
                              overflow: 'hidden'
                          }}>
                             <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.business_name || 'N/A'}</Typography>
                             <Typography variant="caption" sx={{ color: '#546E7A' }}>{row.business_address || ''}</Typography>
                          </TableCell>
                        )}

                        {showSource && (
                          <TableCell sx={{
                            color: getSourceColor(row.source),
                            fontWeight: 500,
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textAlign: 'center'
                          }}>
                            <SourceLogo source={row.source} />
                          </TableCell>
                        )}

                        {showReviewer && (
                          <TableCell sx={{
                            color: '#0A2540',
                            fontWeight: 500,
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            whiteSpace: 'normal',
                            overflow: 'hidden'
                          }}>{row.profile_name}</TableCell>
                        )}
                        
                        {showDate && (
                          <TableCell sx={{
                            color: '#546E7A',
                            fontSize: { xs: '0.875rem', md: '0.95rem' },
                            width: { xs: '70px', sm: '110px', md: '120px' },
                            minWidth: { xs: '70px', sm: '110px', md: '120px' },
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            overflow: 'hidden',
                            textOverflow: { xs: 'clip', sm: 'ellipsis' },
                            lineHeight: { xs: 1.2, sm: 'normal' }
                          }}>
                            {isSmallScreen && !isLandscape ? (
                              <>
                                {new Date(row.estimated_datetime).toLocaleDateString("en-GB").split('/').slice(0, 2).join('/')}/
                                <br />
                                {new Date(row.estimated_datetime).toLocaleDateString("en-GB").split('/')[2]}
                              </>
                            ) : (
                              new Date(row.estimated_datetime).toLocaleDateString("en-GB")
                            )}
                          </TableCell>
                        )}
                        
                        {showRatingStars && (
                          <TableCell align="center" sx={{
                            width: { xs: '65px', sm: '120px' },
                            padding: { xs: '8px 6px', sm: '16px' },
                            overflow: 'visible'
                          }}>
                            {isSmallScreen ? (
                              row.rating !== undefined && (
                                <Typography sx={{
                                  color: '#0A2540',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.875rem', md: '1rem' }
                                }}>
                                  {row.rating}
                                </Typography>
                              )
                            ) : (
                              <Rating
                                value={row.rating}
                                readOnly
                                precision={0.5}
                                size="small"
                                sx={{
                                  color: '#1976d2',
                                  '& .MuiRating-iconEmpty': {
                                    color: 'rgba(25, 118, 210, 0.3)'
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                        )}

                        {/* Review cell is always visible */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#0A2540',
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              lineHeight: { 
                                xs: 1.4,
                                sm: 1.5,
                                md: 1.6 
                              }
                            }}
                          >
                            {highlightReviewText(
                              row.review_text,
                              row.analyses, // Pass analyses from the row
                              currencySymbol,
                              view,
                              rowTurnover,
                              highlightCategory,
                              highlightSubject
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount} // Use dynamic column count
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        color: '#546E7A',
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {'No reviews found matching the current filters.'}
                    </TableCell>
                  </TableRow>
                )}
                {/* Loading indicator for infinite scroll (when fetching next page) */}
                {isTableReviewsLoading && finalReviewsToDisplay.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 2 }}>
                      <Typography>Loading more reviews...</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default ReviewsTable;
